import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RestApiModel } from '../../../shared/model/rest-api.model';
import { SortUtils } from '../../../shared/utils/sort.utils';
import { ProductSearchModel } from '../model/product-search.model';
import { LaborInformation, ProductModel } from '../model/product.model';

export class ServiceHelper {
  public deletion: DeleteClass;
  public addition: SaveClass;
  public mapData: MappedData;
  public utils: Utils;
  constructor(private http: HttpClient) {
    this.deletion = new DeleteClass(http);
    this.addition = new SaveClass(http);
    this.mapData = new MappedData();
    this.utils = new Utils();
  }
}

const PRODUCT_LINES_OF_SERVICE_URL = RestApiModel.PROXY_API_URL + '/product/';

class SaveClass {
  constructor(private http: HttpClient) {}

  /**
   * Create a Line of Service into a Product
   * @param {string} productId
   * @param {string} losId
   * @returns {Observable<any>}
   */
  protected saveLineOfServiceProduct = (
    productId: string,
    losId: string
  ): Observable<any> => {
    return this.http
      .post(
        PRODUCT_LINES_OF_SERVICE_URL + productId + '/lineOfService/' + losId,
        null
      )
      .map((res: any) => res);
  };

  /**
   * Adds a Product Line into a Product
   * @param {string} productId
   * @param {string} productLineId
   * @returns {Observable<any>}
   */
  protected saveProductLineProduct = (
    productId: string,
    productLineId: string
  ): Observable<any> => {
    return this.http
      .post(
        RestApiModel.PROXY_API_URL +
          '/product/' +
          productId +
          '/productLine/' +
          productLineId,
        null
      )
      .map((res: any) => res);
  };

  /**
   * Adds a Products Supplied By Customer  into a Product.
   * @param {string} productId
   * @param {string} productLineId
   * @returns {Observable<any>}
   */
  protected savePSBCForProduct = (
    productId: string,
    productLineId: string
  ): Observable<any> => {
    return this.http
      .post(
        RestApiModel.PROXY_API_URL +
          '/product/' +
          productId +
          '/productsSuppliedByCustomer/' +
          productLineId,
        null
      )
      .map((res: any) => res);
  };
}

class DeleteClass {
  constructor(private http: HttpClient) {}
  /**
   * Delete a Line of Service from a Product
   * @param {string} productId
   * @param {string} losId
   * @returns {Observable<any>}
   */
  protected deleteLineOfServiceProduct = (
    productId: string,
    losId: string
  ): Observable<any> => {
    return this.http
      .delete(
        PRODUCT_LINES_OF_SERVICE_URL + productId + '/lineOfService/' + losId
      )
      .map((res: any) => res);
  };

  /**
   * Delete a Product Line from a Product
   * @param {string} productId
   * @param {string} productLineId
   * @returns {Observable<any>}
   */
  protected deleteProductLineProduct = (
    productId: string,
    productLineId: string
  ): Observable<any> => {
    return this.http
      .delete(
        RestApiModel.PROXY_API_URL +
          '/product/' +
          productId +
          '/productLine/' +
          productLineId
      )
      .map((res: any) => res);
  };

  /**
   * Delete a Products Supplied By Customer from a Product.
   * @param {string} productId
   * @param {string} productLineId
   * @returns {Observable<any>}
   */
  protected deletePSBCForProduct = (
    productId: string,
    productLineId: string
  ): Observable<any> => {
    return this.http
      .delete(
        RestApiModel.PROXY_API_URL +
          '/product/' +
          productId +
          '/productsSuppliedByCustomer/' +
          productLineId
      )
      .map((res: any) => res);
  };
}

class MappedData {
  protected productTypeId = Array<any>();
  /**
   * Map the given result into the product and  part model.
   * @param {ProductModel} productModel
   * @param {any} res
   * @returns {ProductModel}
   */
  protected mapPartAndProductModel(
    productModel: ProductModel,
    res: any
  ): ProductModel {
    productModel = new ProductModel();
    if (res[0]) {
      productModel = res[0];
      productModel.startDate = res[0].startDate
        ? new Date(res[0].startDate)
        : null;
      productModel.endDate = res[0].endDate ? new Date(res[0].endDate) : null;
      if (productModel.makeId <= 0) {
        productModel.makeId = null;
      }
    }
    productModel.laborInformation = new LaborInformation();
    /*if (res[1] && res[1].results) {
			productModel.laborInformation.maximumMaterialCost = res[1].results.maximumMaterialCost;
			productModel.laborInformation.minimumMaterialCost = res[1].results.minimumMaterialCost;
			productModel.laborInformation.isTruckStock = res[1].results.isTruckStock;
			productModel.laborInformation.isDisplayedOnLaborInformationPortal = res[1].results.isDisplayedOnLaborInformationPortal;
		}*/
    return productModel;
  }

  /**
   * Map the given result into the search model.
   * @param {ProductSearchModel} searchModel
   * @param {any} res
   * @returns {ProductSearchModel}
   */
  protected mapSearchResult(
    searchModel: ProductSearchModel,
    res: any
  ): ProductSearchModel {
    searchModel.count = res.rows;
    searchModel.pageNumber = res.pageNumber;
    searchModel.totalCount = res.count;
    searchModel.totalPages = res.totalPages;
    return searchModel;
  }
}

class Utils {
  /**
   * Maps the given response
   * @param {any[]} response
   * @param {string} propertyToMap
   * @param {string} propertyToSortBy
   */
  public mapResult(
    response: any[],
    propertyToMap: string,
    propertyToSortBy: string
  ): any[] {
    // Model the result to be mapped.
    const mapRes = item => {
      return { id: item.id, [propertyToSortBy]: item[propertyToMap] };
    };
    // Mapping the result
    let result = response.map(mapRes);
    // Return the result sorted by the given property "propertyToSortBy"
    return this.sortResult(result, propertyToSortBy);
  }

  /**
   * Sorts the given result
   * @params {any} result
   * @params {string} propertyToSortBy
   */
  public sortResult(result, propertyToSortBy: string): any[] {
    return !result || result.length === 0
      ? []
      : result.sort((a, b) =>
          SortUtils.compareByProperty(a, b, propertyToSortBy)
        );
  }

  /**
   * Validates if the given value is date object.
   * @param {string} date
   * @returns {Boolean}
   */
  protected isValidateDateObject(date: string): boolean {
    return new Date(date).toString() !== 'Invalid Date';
  }

  /**
   * Gets a date parameter from the filter model.
   * @param {ProductSearchModel} searchModel
   * @param {any} p
   * @returns {Boolean}
   */
  protected getDateParam(searchModel: ProductSearchModel, p: any): string {
    let dateParam = JSON.stringify(searchModel.filterModel[p]);
    dateParam = dateParam.replace(/\"/g, '');
    return p + '=' + dateParam + '&';
  }
}
